/** Default poll interval when checking for a newer deployed app version. */
export const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Max random delay added to each poll tick to spread concurrent client requests. */
export const POLL_INTERVAL_SPLAY_MS = 60 * 1000;

/**
 * Returns the next poll delay with optional jitter.
 * @param pollIntervalMs Base interval between polls.
 * @param pollSplayMs Max extra random delay, or 0 to disable splay.
 */
export function getPollDelayMs(
  pollIntervalMs: number,
  pollSplayMs: number,
): number {
  if (pollSplayMs <= 0) {
    return pollIntervalMs;
  }

  const randomValues = new Uint32Array(1);
  crypto.getRandomValues(randomValues);
  const randomValue = randomValues[0] ?? 0;

  return pollIntervalMs + (randomValue % (pollSplayMs + 1));
}

// After deploy, lazy import() may fail before the next poll. Match common
// bundler messages only, not every window error.
const LAZY_LOAD_ERROR_PATTERN =
  /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module/i;

function isLazyLoadError(event: ErrorEvent): boolean {
  const message = event.message || '';
  const errorMessage = event.error instanceof Error ? event.error.message : '';

  return LAZY_LOAD_ERROR_PATTERN.test(`${message} ${errorMessage}`);
}

/**
 * Runs an initial check, then polls and rechecks on tab focus and lazy-load errors.
 * Each poll tick uses base interval plus up to POLL_INTERVAL_SPLAY_MS jitter.
 * @returns Unsubscribe function for the poll timer and event listeners.
 */
export function subscribeToVersionMonitor(
  check: () => void | Promise<void>,
  pollIntervalMs: number,
  pollSplayMs: number = POLL_INTERVAL_SPLAY_MS,
): () => void {
  void check();

  let pollTimeoutId: number | undefined;

  const scheduleNextPoll = (): void => {
    const delayMs = getPollDelayMs(pollIntervalMs, pollSplayMs);
    pollTimeoutId = window.setTimeout(() => {
      void check();
      scheduleNextPoll();
    }, delayMs);
  };

  scheduleNextPoll();

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  const handleWindowError = (event: ErrorEvent): void => {
    if (isLazyLoadError(event)) {
      void check();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('error', handleWindowError);

  return () => {
    if (pollTimeoutId !== undefined) {
      window.clearTimeout(pollTimeoutId);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('error', handleWindowError);
  };
}
